import { useCallback, useEffect, useState } from 'react';
import { projectsApi } from '@/api/projects.api';
import { EmployeeSelect, ProjectSelect } from '@/components/selects';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { Input } from '@/components/ui/Input';
import { DatePicker } from '@/components/ui/DatePicker';
import { DateRangeFields } from '@/components/ui/DateRangeFields';
import { Select } from '@/components/ui/Select';
import { Modal } from '@/components/ui/Modal';
import { usePagination } from '@/hooks/usePagination';
import { useEmployeeOptions } from '@/hooks/useEmployeeOptions';
import { useProjectList } from '@/modules/projects/shared/useProjectList';
import { showApiError } from '@/utils/apiError';
import { usePrompt } from '@/components/ui/PromptDialog';
import { notify } from '@/core/toast/notify';

interface TimesheetRow {
  id: number;
  employeeId: number;
  weekStartDate: string;
  weekEndDate: string;
  totalHours: number;
  approvalStatus: string;
}

export function ProjectsTimesheetsPage() {
  const { prompt, dialog: promptDialog } = usePrompt();
  const { getLabel } = useEmployeeOptions();
  const { projects } = useProjectList();
  const pagination = usePagination();
  const [data, setData] = useState<TimesheetRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('PENDING');
  const [logOpen, setLogOpen] = useState(false);
  const [submitOpen, setSubmitOpen] = useState(false);
  const [logForm, setLogForm] = useState({ employeeId: '', projectId: '', hours: 8, entryDate: new Date().toISOString().slice(0, 10), billable: true });
  const [submitForm, setSubmitForm] = useState({ employeeId: '', weekStartDate: '', weekEndDate: '' });
  const [billProjectId, setBillProjectId] = useState<number | ''>('');
  const [billFrom, setBillFrom] = useState('');
  const [billTo, setBillTo] = useState('');
  const [billing, setBilling] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await projectsApi.listTimesheets({ page: pagination.page, size: pagination.size }, statusFilter || undefined);
      setData(res.content as TimesheetRow[]);
      pagination.applyPageResponse(res);
    } catch (err) {
      showApiError(err, 'Failed to load timesheets');
    } finally {
      setLoading(false);
    }
  }, [pagination, statusFilter]);

  useEffect(() => { void load(); }, [load]);

  const approve = async (id: number) => {
    try {
      await projectsApi.approveTimesheet(id);
      notify.success('Timesheet approved');
      load();
    } catch (err) {
      showApiError(err, 'Approve failed');
    }
  };

  const reject = async (id: number) => {
    const notes = (await prompt({ title: 'Reject timesheet', label: 'Rejection notes (optional)' })) ?? undefined;
    try {
      await projectsApi.rejectTimesheet(id, notes);
      notify.success('Timesheet rejected');
      load();
    } catch (err) {
      showApiError(err, 'Reject failed');
    }
  };

  const logTime = async () => {
    try {
      await projectsApi.logTime({
        employeeId: Number(logForm.employeeId),
        projectId: logForm.projectId ? Number(logForm.projectId) : undefined,
        entryDate: logForm.entryDate,
        hours: logForm.hours,
        billable: logForm.billable,
      });
      notify.success('Time logged');
      setLogOpen(false);
    } catch (err) {
      showApiError(err, 'Log time failed');
    }
  };

  const billToFinance = async () => {
    if (billProjectId === '') {
      notify.error('Select a project');
      return;
    }
    if (!billFrom || !billTo) {
      notify.error('Enter billing date range');
      return;
    }
    setBilling(true);
    try {
      const result = await projectsApi.billTime(Number(billProjectId), billFrom, billTo);
      notify.success(`Finance invoice ${result.invoiceNumber} created (${result.lineCount} lines)`);
    } catch (err) {
      showApiError(err, 'Bill time failed — ensure Finance module and billable hours exist');
    } finally {
      setBilling(false);
    }
  };

  const submitWeek = async () => {
    try {
      await projectsApi.submitTimesheet({
        employeeId: Number(submitForm.employeeId),
        weekStartDate: submitForm.weekStartDate,
        weekEndDate: submitForm.weekEndDate,
      });
      notify.success('Timesheet submitted');
      setSubmitOpen(false);
      load();
    } catch (err) {
      showApiError(err, 'Submit failed');
    }
  };

  const columns: Column<TimesheetRow>[] = [
    { key: 'employeeId', header: 'Employee', render: (row) => getLabel(row.employeeId, true) },
    { key: 'weekStartDate', header: 'Week start' },
    { key: 'weekEndDate', header: 'Week end' },
    { key: 'totalHours', header: 'Hours' },
    { key: 'approvalStatus', header: 'Status' },
    {
      key: 'actions',
      header: 'Actions',
      render: (row) => row.approvalStatus === 'PENDING' ? (
        <div className="flex gap-2">
          <Button variant="secondary" className="text-xs py-1 px-2" onClick={() => void approve(row.id)}>Approve</Button>
          <Button variant="danger" className="text-xs py-1 px-2" onClick={() => void reject(row.id)}>Reject</Button>
        </div>
      ) : null,
    },
  ];

  return (
    <div>
      {promptDialog}
      <PageHeader title="Timesheets" description="Log time, submit weekly timesheets, and approve hours"
        breadcrumbs={[{ label: 'Org', to: '/org' }, { label: 'Projects', to: '/projects' }, { label: 'Timesheets' }]}
        actions={<>
          <Button variant="secondary" onClick={() => setLogOpen(true)}>Log time</Button>
          <Button onClick={() => setSubmitOpen(true)}>Submit week</Button>
        </>} />
      <div className="card mb-4 p-4 space-y-3">
        <h3 className="text-sm font-semibold">Bill approved time to Finance</h3>
        <p className="text-xs text-slate-500">
          Creates a Finance invoice from billable time entries in the date range (`ProjectTimeBillingService`).
        </p>
        <div className="flex flex-wrap gap-3 items-end">
          <Select
            label="Project"
            placeholder="Select project"
            value={String(billProjectId ?? '')}
            onChange={(v) => setBillProjectId(v ? Number(v) : '')}
            options={projects.map((p) => ({ value: String(p.id), label: p.projectName }))}
          />
          <DateRangeFields from={billFrom} to={billTo} onFromChange={setBillFrom} onToChange={setBillTo} />
          <Button loading={billing} onClick={() => void billToFinance()}>
            Bill to Finance
          </Button>
        </div>
      </div>

      <div className="mb-4">
        <Select
          placeholder="All"
          value={statusFilter}
          onChange={(v) => { setStatusFilter(v); pagination.resetPage(); }}
          options={[
            { value: 'PENDING', label: 'Pending' },
            { value: 'APPROVED', label: 'Approved' },
            { value: 'REJECTED', label: 'Rejected' },
          ]}
        />
      </div>
      <DataTable columns={columns} data={data} keyField="id" loading={loading}
        page={pagination.page} totalPages={pagination.totalPages} totalElements={pagination.totalElements} onPageChange={pagination.setPage} />
      <Modal open={logOpen} onClose={() => setLogOpen(false)} title="Log time" footer={<><Button variant="secondary" onClick={() => setLogOpen(false)}>Cancel</Button><Button onClick={() => void logTime()}>Save</Button></>}>
        <div className="space-y-3">
          <EmployeeSelect value={logForm.employeeId} onChange={(v) => setLogForm({ ...logForm, employeeId: v })} />
          <ProjectSelect value={logForm.projectId} onChange={(v) => setLogForm({ ...logForm, projectId: v })} />
          <DatePicker label="Date" value={logForm.entryDate} onChange={(v) => setLogForm({ ...logForm, entryDate: v })} />
          <Input label="Hours" type="number" value={logForm.hours} onChange={(e) => setLogForm({ ...logForm, hours: Number(e.target.value) })} />
        </div>
      </Modal>
      <Modal open={submitOpen} onClose={() => setSubmitOpen(false)} title="Submit timesheet" footer={<><Button variant="secondary" onClick={() => setSubmitOpen(false)}>Cancel</Button><Button onClick={() => void submitWeek()}>Submit</Button></>}>
        <div className="space-y-3">
          <EmployeeSelect value={submitForm.employeeId} onChange={(v) => setSubmitForm({ ...submitForm, employeeId: v })} />
          <DateRangeFields
            from={submitForm.weekStartDate}
            to={submitForm.weekEndDate}
            onFromChange={(v) => setSubmitForm({ ...submitForm, weekStartDate: v })}
            onToChange={(v) => setSubmitForm({ ...submitForm, weekEndDate: v })}
            fromLabel="Week start"
            toLabel="Week end"
          />
        </div>
      </Modal>
    </div>
  );
}
